import React, { forwardRef, useCallback, useImperativeHandle } from 'react';

import { Control, FieldErrors, UseFormRegister, useFieldArray } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { FlightLegSegments } from './CustomOfferFlightLegSegments';

export type FlightLegsFormValues = {
  item: {
    flight_legs?: Array<App.CustomOfferFlightLeg>;
  };
};

export type FlightLegsFormHandles<TFieldValues extends FlightLegsFormValues = FlightLegsFormValues> = {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
};

/**
 * Narrows a wider form's handles to the legs slice. react-hook-form's Control is invariant
 * in its form type, so the cast is unavoidable and this is the one place it lives.
 */
export function asFlightLegsForm<TFieldValues extends FlightLegsFormValues>(
  handles: FlightLegsFormHandles<TFieldValues>,
): FlightLegsFormHandles {
  return handles as unknown as FlightLegsFormHandles;
}

type Props = FlightLegsFormHandles & {
  isSubmitting: boolean;
  /** Marks the fields the LEBT schema requires; validation itself lives in the schema. */
  isBusinessTraveller?: boolean;
};

export type FlightLegsSectionRef = {
  addFlightLeg: (leg: App.CustomOfferFlightLeg) => void;
  removeFlightLeg: (index: number) => void;
  clearAllLegs: () => void;
};

export const FlightLegsSection = forwardRef<FlightLegsSectionRef, Props>(
  ({ control, register, errors, isSubmitting, isBusinessTraveller = false }, ref) => {
    const {
      fields: flightLegs,
      append: addFlightLeg,
      remove: removeFlightLeg,
    } = useFieldArray({ control, name: 'item.flight_legs' });

    // Get the flight_legs error (array-level validation error)
    const flightLegsError = errors.item?.flight_legs;
    // The error could be on the array itself (root) or as a message property
    const flightLegsErrorMessage =
      flightLegsError?.root?.message || flightLegsError?.message || (flightLegsError as any)?.message;

    useImperativeHandle(ref, () => ({
      addFlightLeg: (leg: App.CustomOfferFlightLeg) => addFlightLeg(leg),
      removeFlightLeg: (index: number) => removeFlightLeg(index),
      clearAllLegs: () => {
        const count = flightLegs.length;
        for (let i = count - 1; i >= 0; i--) {
          removeFlightLeg(i);
        }
      },
    }));

    const handleAddNewLeg = useCallback(() => {
      const newLeg: App.CustomOfferFlightLeg = {
        segments: [
          {
            airline: '',
            flightNumber: '',
            bookingClass: '',
            departureDate: '',
            departureAirport: '',
            departureAirportName: '',
            arrivalAirport: '',
            arrivalAirportName: '',
            departureTime: '',
            arrivalTime: '',
            arrivalDate: '',
          },
        ],
      };
      addFlightLeg(newLeg);
    }, [addFlightLeg]);

    const handleRemoveLeg = useCallback(
      (index: number) => {
        removeFlightLeg(index);
      },
      [removeFlightLeg],
    );

    return (
      <>
        {flightLegs.map((leg, legIndex) => (
          <Box key={leg.id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2, mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" gutterBottom>
                Leg {legIndex + 1}
              </Typography>
              {!isSubmitting && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  title="Remove leg"
                  data-testid={`remove-leg-btn-${legIndex}`}
                  onClick={() => handleRemoveLeg(legIndex)}
                  startIcon={<DeleteIcon />}
                >
                  Remove Leg
                </Button>
              )}
            </Stack>
            <FlightLegSegments
              legIndex={legIndex}
              control={control}
              register={register}
              errors={errors}
              isSubmitting={isSubmitting}
              isBusinessTraveller={isBusinessTraveller}
            />
          </Box>
        ))}
        {flightLegsErrorMessage && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} data-testid="flight-legs-error">
            {flightLegsErrorMessage}
          </Alert>
        )}
        {!isSubmitting && (
          <Stack direction="row" justifyContent="space-between">
            <Button
              color="primary"
              title="Add flight leg"
              data-testid="add-leg-btn"
              variant="text"
              size="small"
              onClick={handleAddNewLeg}
              startIcon={<AddIcon />}
            >
              Add Leg
            </Button>
          </Stack>
        )}
      </>
    );
  },
);

FlightLegsSection.displayName = 'FlightLegsSection';
